import React, {useDebugValue} from 'react';

import classNames from 'classnames';

import {
    useSearchFormContext,
    useTrackingContext
} from '../../../hooks';

import {
    shouldGuestsFieldAutoSubmitOnClose,
    showGuestsInputFieldError,
    showGuestsInputIcon,
    submitForm
} from '../../../utils';

import {SearchIcon} from '../../SearchIcon';

const classNamespace = 'search-form';
const fieldClassName = `${classNamespace}__field`;
const inputFieldClassName = `${classNamespace}__input-field`;
const inputFieldErrorClassName = `${inputFieldClassName}--error`;
const detailsFieldClassName = `${classNamespace}__details-field`;
const detailsInputFieldClassName = `${classNamespace}__details-input-field`;
const guestsDetailsInputFieldClassName = `${classNamespace}__guests-details-input-field`;

// eslint-disable-next-line complexity
function useComponent() {
    const searchFormContext = useSearchFormContext();

    const {
        system,
        media,
        media: {
            settings: {
                isMobile
            }
        },
        abacus,
        edap,
        search,
        form,
        destination,
        destination: {
            actions: {
                setIsTypeaheadInputFocus
            }
        },
        dates,
        dates: {
            actions: {
                setIsOpen: setDatesIsOpen
            }
        },
        guests,
        guests: {
            actions: {
                setIsOpen: setGuestsIsOpen,
                setCurrentValuesLatest,
                setLatestValues,
                setAllValuesDefault
            },
            events: {
                onChange,
                onClear,
                onClose,
                onExit,
                onOpen
            },
            options: {
                actionLocation,
                adultsMax,
                adultsMin,
                centered,
                childrenMax,
                childrenMin,
                disableTextInput,
                fadeOut,
                fadeOutDuration,
                id,
                inputIconName,
                name,
                placement,
                positionIconRight,
                showClearButton,
                showContinue,
                showPetOption,
                wdioPrefix
            },
            settings: {
                disableButton,
                isOpen,
                readOnly
            },
            translations: {
                adultText,
                adultsText,
                childText,
                childrenText,
                guestText,
                guestsText,
                petsText,
                label,
                modalTitle,
                petLabel,
                petNoLabel,
                petYesLabel,
                petMessage,
                applyButtonLabel,
                clearButtonLabel,
                continueButtonLabel
            },
            latestValues: {
                adultsCount,
                childrenCount,
                petIncluded
            }
        }
    } = searchFormContext;

    let {
        guests: {
            translations: {
                guestLabel,
                adultsLabel,
                childrenLabel,
            }
        }
    } = searchFormContext;

    const tracking = useTrackingContext();

    const className = classNames(fieldClassName, inputFieldClassName, {
        [inputFieldErrorClassName]: showGuestsInputFieldError(media, form, guests)
    }, detailsFieldClassName, detailsInputFieldClassName, guestsDetailsInputFieldClassName);

    const inputIcon = showGuestsInputIcon(form, guests) && (
        <SearchIcon iconName={inputIconName}/>
    ) || null;

    const guestsCount = adultsCount + childrenCount;

    adultsLabel = adultsLabel || `${adultsCount} ${adultsCount === 1 ? adultText : adultsText}`;
    childrenLabel = childrenLabel || `${childrenCount} ${childrenCount === 1 ? childText : childrenText}`;
    guestLabel = guestLabel || (
        guestsCount
            ? `${guestsCount} ${guestsCount === 1 ? guestText : guestsText}${petIncluded ? `, ${petsText}` : ''}`
            : ''
    );

    const formSuffixElement = petIncluded && (
        <p className="text-center text-muted">{petMessage}</p>
    ) || null;

    function handleChange(...args) {
        const [
            key,
            value
        ] = args;

        const changedValue = {
            [key]: value
        };

        setLatestValues(changedValue);

        onChange(...args);
    }

    function handleClear(...args) {
        setAllValuesDefault();

        onClear(...args);
    }

    function handleClose(...args) {
        setCurrentValuesLatest();

        setGuestsIsOpen(false);

        if (shouldGuestsFieldAutoSubmitOnClose(media, form, guests, abacus, system, destination, dates)) {
            submitForm(tracking, system, abacus, edap, search, form, destination, dates, guests);
        }

        onClose(...args);
    }

    function handleExit(...args) {
        setCurrentValuesLatest();

        setGuestsIsOpen(false);

        onExit(...args);
    }

    function handleOpen(...args) {
        setIsTypeaheadInputFocus(false);
        setDatesIsOpen(false);
        setGuestsIsOpen(true);

        onOpen(...args);
    }

    const result = {
        className,
        actionLocation,
        adultsCount,
        adultsLabel,
        adultsMax,
        adultsMin,
        applyButtonLabel,
        centered,
        childrenCount,
        childrenLabel,
        childrenMax,
        childrenMin,
        clearButtonLabel,
        continueButtonLabel,
        disableButton,
        disableTextInput,
        fadeOut,
        fadeOutDuration,
        formSuffixElement,
        guestLabel,
        id,
        inputIcon,
        isMobile,
        isOpen,
        label,
        modalTitle,
        name,
        onChange: handleChange,
        onClear: handleClear,
        onClose: handleClose,
        onExit: handleExit,
        onOpen: handleOpen,
        petIncluded,
        petLabel,
        petNoLabel,
        petYesLabel,
        placement,
        positionIconRight,
        readOnly,
        showClearButton,
        showContinue,
        showPetOption,
        wdioPrefix
    };

    useDebugValue(result);

    return result;
}

export {useComponent};
